/**
 * SituationPanel Component
 * 
 * Displays the Customer Situation panel with multiple sections:
 * - Resources (top left): Available resources that match solution inputs
 * - Needs (bottom left): Customer needs that benefits should address
 * - Actor (center): The decision maker with priorities and motivations
 * - Inputs (top right): Situation-specific inputs
 * - Outputs (bottom right): Situation-specific outputs
 */

import React, { useState } from 'react';
import { 
  Button, 
  Modal, 
  Form, 
  Input, 
  Select, 
  InputNumber,
  Checkbox,
  Tooltip,
  Empty,
  message 
} from 'antd';
import {
  EnvironmentOutlined,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  DatabaseOutlined,
  BulbOutlined,
  ImportOutlined,
  ExportOutlined,
  LinkOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { 
  CustomerSituation, 
  SituationResource, 
  SituationNeed,
  SituationInput,
  SituationOutput,
  ConnectionLink,
} from '../../types/marketFit';
import { useMarketFit } from '../../contexts/MarketFitContext';
import ActorAlignmentPanel from './ActorAlignmentPanel';
import styles from './SituationPanel.module.css';

interface SituationPanelProps {
  situation: CustomerSituation | null;
  resourceLinks: ConnectionLink[];
  needLinks: ConnectionLink[];
  onResourceSelect?: (resourceId: string) => void;
  onNeedSelect?: (needId: string) => void;
  selectedResourceId?: string | null;
  selectedNeedId?: string | null;
}

// ============================================================================
// SUB-COMPONENTS
// ============================================================================

interface ResourceItemProps {
  resource: SituationResource;
  link?: ConnectionLink;
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const ResourceItem: React.FC<ResourceItemProps> = ({
  resource,
  link,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}) => {
  const getLinkStatus = () => {
    if (!link) return { class: styles.itemUnlinked, icon: null };
    if (link.matchType === 'full') return { 
      class: styles.itemLinked, 
      icon: <CheckCircleOutlined className={styles.linkIconFull} /> 
    };
    if (link.matchType === 'partial') return { 
      class: styles.itemPartial, 
      icon: <ExclamationCircleOutlined className={styles.linkIconPartial} /> 
    };
    return { class: styles.itemUnlinked, icon: null };
  };

  const linkStatus = getLinkStatus();

  return (
    <div 
      className={`${styles.item} ${linkStatus.class} ${isSelected ? styles.itemSelected : ''}`}
      onClick={onSelect}
    >
      <div className={styles.itemContent}>
        <DatabaseOutlined className={styles.itemIcon} />
        <div className={styles.itemInfo}>
          <span className={styles.itemName}>{resource.name}</span>
          {resource.description && (
            <span className={styles.itemDescription}>{resource.description}</span>
          )}
        </div>
      </div>
      <div className={styles.itemMeta}>
        {linkStatus.icon}
        {!resource.isExisting && (
          <Tooltip title="New resource required">
            <WarningOutlined className={styles.newResourceWarning} />
          </Tooltip>
        )}
        {resource.cost > 0 && (
          <span className={styles.costBadge}>
            ${resource.cost.toLocaleString()}
          </span>
        )}
        <div className={styles.itemActions}>
          <Tooltip title="Edit">
            <button 
              className={styles.itemActionButton}
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button 
              className={`${styles.itemActionButton} ${styles.itemActionButtonDelete}`}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

interface NeedItemProps {
  need: SituationNeed;
  links: ConnectionLink[];
  isSelected: boolean;
  onSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

const NeedItem: React.FC<NeedItemProps> = ({
  need,
  links,
  isSelected,
  onSelect,
  onEdit,
  onDelete,
}) => {
  const linkedCount = links.filter(l => l.toId === need.id).length;
  
  const getLinkStatus = () => {
    if (linkedCount === 0) return { class: styles.itemUnlinked, icon: null };
    const hasFullMatch = links.some(l => l.toId === need.id && l.matchType === 'full');
    if (hasFullMatch) return { 
      class: styles.itemLinked, 
      icon: <CheckCircleOutlined className={styles.linkIconFull} /> 
    };
    return { 
      class: styles.itemPartial, 
      icon: <ExclamationCircleOutlined className={styles.linkIconPartial} /> 
    };
  };

  const linkStatus = getLinkStatus();
  
  const getPriorityClass = () => {
    switch (need.priority) {
      case 'high': return styles.itemTagHigh;
      case 'medium': return styles.itemTagMedium;
      case 'low': return styles.itemTagLow;
      default: return '';
    }
  };

  return (
    <div 
      className={`${styles.item} ${linkStatus.class} ${isSelected ? styles.itemSelected : ''}`}
      onClick={onSelect}
    >
      <div className={styles.itemContent}>
        <BulbOutlined className={styles.itemIcon} />
        <div className={styles.itemInfo}>
          <span className={styles.itemName}>{need.name}</span>
          {need.description && (
            <span className={styles.itemDescription}>{need.description}</span>
          )}
        </div>
      </div>
      <div className={styles.itemMeta}>
        {linkStatus.icon}
        {linkedCount > 0 && (
          <Tooltip title={`Addressed by ${linkedCount} benefit${linkedCount > 1 ? 's' : ''}`}>
            <span className={styles.linkCount}>
              <LinkOutlined /> {linkedCount}
            </span>
          </Tooltip>
        )}
        <span className={`${styles.itemTag} ${getPriorityClass()}`}>
          {need.priority}
        </span>
        <div className={styles.itemActions}>
          <Tooltip title="Edit">
            <button 
              className={styles.itemActionButton}
              onClick={(e) => { e.stopPropagation(); onEdit(); }}
            >
              <EditOutlined />
            </button>
          </Tooltip>
          <Tooltip title="Delete">
            <button 
              className={`${styles.itemActionButton} ${styles.itemActionButtonDelete}`}
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
            >
              <DeleteOutlined />
            </button>
          </Tooltip>
        </div>
      </div>
    </div>
  );
};

interface SimpleItemProps {
  item: SituationInput | SituationOutput;
  icon: React.ReactNode;
  onEdit: () => void;
  onDelete: () => void;
}

const SimpleItem: React.FC<SimpleItemProps> = ({
  item,
  icon,
  onEdit,
  onDelete,
}) => (
  <div className={styles.simpleItem}>
    <div className={styles.itemContent}>
      {icon}
      <div className={styles.itemInfo}>
        <span className={styles.itemName}>{item.name}</span>
        {item.description && (
          <span className={styles.itemDescription}>{item.description}</span>
        )}
      </div>
    </div>
    <div className={styles.itemActions}>
      <Tooltip title="Edit">
        <button 
          className={styles.itemActionButton}
          onClick={(e) => { e.stopPropagation(); onEdit(); }}
        >
          <EditOutlined />
        </button>
      </Tooltip>
      <Tooltip title="Delete">
        <button 
          className={`${styles.itemActionButton} ${styles.itemActionButtonDelete}`}
          onClick={(e) => { e.stopPropagation(); onDelete(); }}
        >
          <DeleteOutlined />
        </button>
      </Tooltip>
    </div>
  </div>
);

// ============================================================================
// MAIN COMPONENT
// ============================================================================

export const SituationPanel: React.FC<SituationPanelProps> = ({
  situation,
  resourceLinks,
  needLinks,
  onResourceSelect,
  onNeedSelect,
  selectedResourceId,
  selectedNeedId,
}) => {
  const { 
    actor,
    addSituationResource, 
    updateSituationResource, 
    removeSituationResource,
    addSituationNeed,
    updateSituationNeed,
    removeSituationNeed,
  } = useMarketFit();

  const [resourceModalVisible, setResourceModalVisible] = useState(false);
  const [needModalVisible, setNeedModalVisible] = useState(false);
  const [editingResource, setEditingResource] = useState<SituationResource | null>(null);
  const [editingNeed, setEditingNeed] = useState<SituationNeed | null>(null);
  
  const [resourceForm] = Form.useForm();
  const [needForm] = Form.useForm();

  // Resource handlers
  const handleAddResource = () => {
    setEditingResource(null);
    resourceForm.resetFields();
    resourceForm.setFieldsValue({ isExisting: true, cost: 0 });
    setResourceModalVisible(true);
  };

  const handleEditResource = (resource: SituationResource) => {
    setEditingResource(resource);
    resourceForm.setFieldsValue(resource);
    setResourceModalVisible(true);
  };

  const handleDeleteResource = (resourceId: string) => {
    Modal.confirm({
      title: 'Delete Resource',
      content: 'Are you sure you want to delete this resource? Any connection links will also be removed.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        removeSituationResource(resourceId);
        void message.success('Resource deleted');
      },
    });
  };

  const handleResourceSubmit = () => {
    resourceForm.validateFields().then(values => {
      if (editingResource) {
        updateSituationResource(editingResource.id, values);
        void message.success('Resource updated');
      } else {
        addSituationResource(values);
        void message.success('Resource added');
      }
      setResourceModalVisible(false);
      resourceForm.resetFields();
    }).catch(() => {
      // Validation failed
    });
  };

  // Need handlers
  const handleAddNeed = () => {
    setEditingNeed(null);
    needForm.resetFields();
    needForm.setFieldsValue({ priority: 'medium' });
    setNeedModalVisible(true);
  };

  const handleEditNeed = (need: SituationNeed) => {
    setEditingNeed(need);
    needForm.setFieldsValue(need);
    setNeedModalVisible(true);
  };

  const handleDeleteNeed = (needId: string) => {
    Modal.confirm({
      title: 'Delete Need',
      content: 'Are you sure you want to delete this need? Any connection links will also be removed.',
      okText: 'Delete',
      okButtonProps: { danger: true },
      onOk: () => {
        removeSituationNeed(needId);
        void message.success('Need deleted');
      },
    });
  };

  const handleNeedSubmit = () => {
    needForm.validateFields().then(values => {
      if (editingNeed) {
        updateSituationNeed(editingNeed.id, values);
        void message.success('Need updated');
      } else {
        addSituationNeed(values);
        void message.success('Need added');
      }
      setNeedModalVisible(false);
      needForm.resetFields();
    }).catch(() => {
      // Validation failed
    });
  };

  const resources = situation?.resources ?? [];
  const needs = situation?.needs ?? [];
  const inputs = situation?.inputs ?? [];
  const outputs = situation?.outputs ?? [];

  return (
    <div className={`${styles.panel} ${styles.situationPanel}`}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitle}>
          <EnvironmentOutlined className={styles.panelIcon} />
          <span>Customer Situation</span>
          {situation && <span className={styles.panelBadge}>{situation.situationName}</span>}
        </div>
      </div>

      <div className={styles.panelContent}>
        <div className={styles.situationGrid}>
          {/* Resources Section - Top Left */}
          <div className={`${styles.section} ${styles.resourcesSection}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <DatabaseOutlined className={styles.sectionIcon} />
                <span>Resources</span>
                <span className={styles.sectionCount}>({resources.length})</span>
              </div>
              <Button 
                type="text" 
                size="small" 
                icon={<PlusOutlined />}
                onClick={handleAddResource}
                className={styles.addButton}
              >
                Add
              </Button>
            </div>
            
            <div className={styles.itemList}>
              {resources.length === 0 ? (
                <Empty 
                  description="No resources defined" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className={styles.emptyState}
                />
              ) : (
                resources.map(resource => (
                  <ResourceItem
                    key={resource.id}
                    resource={resource}
                    link={resourceLinks.find(l => l.toId === resource.id)}
                    isSelected={selectedResourceId === resource.id}
                    onSelect={() => onResourceSelect?.(resource.id)}
                    onEdit={() => handleEditResource(resource)}
                    onDelete={() => handleDeleteResource(resource.id)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Needs Section - Bottom Left */}
          <div className={`${styles.section} ${styles.needsSection}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <BulbOutlined className={styles.sectionIcon} />
                <span>Needs</span>
                <span className={styles.sectionCount}>({needs.length})</span>
              </div>
              <Button 
                type="text" 
                size="small" 
                icon={<PlusOutlined />}
                onClick={handleAddNeed}
                className={styles.addButton}
              >
                Add
              </Button>
            </div>
            
            <div className={styles.itemList}>
              {needs.length === 0 ? (
                <Empty 
                  description="No needs defined" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className={styles.emptyState}
                />
              ) : (
                needs.map(need => (
                  <NeedItem
                    key={need.id}
                    need={need}
                    links={needLinks}
                    isSelected={selectedNeedId === need.id}
                    onSelect={() => onNeedSelect?.(need.id)}
                    onEdit={() => handleEditNeed(need)}
                    onDelete={() => handleDeleteNeed(need.id)}
                  />
                ))
              )}
            </div>
          </div>

          {/* Actor Panel - Center */}
          <div className={styles.actorSection}>
            <ActorAlignmentPanel actor={actor} />
          </div>

          {/* Inputs Section - Top Right */}
          <div className={`${styles.section} ${styles.inputsSection}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <ImportOutlined className={styles.sectionIcon} />
                <span>Inputs</span>
                <span className={styles.sectionCount}>({inputs.length})</span>
              </div>
            </div>
            
            <div className={styles.simpleItemList}>
              {inputs.length === 0 ? (
                <Empty 
                  description="No inputs" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className={styles.emptyStateSmall}
                />
              ) : (
                inputs.map(input => (
                  <SimpleItem
                    key={input.id}
                    item={input}
                    icon={<ImportOutlined className={styles.simpleItemIcon} />}
                    onEdit={() => {/* TODO */}}
                    onDelete={() => {/* TODO */}}
                  />
                ))
              )}
            </div>
          </div>

          {/* Outputs Section - Bottom Right */}
          <div className={`${styles.section} ${styles.outputsSection}`}>
            <div className={styles.sectionHeader}>
              <div className={styles.sectionTitle}>
                <ExportOutlined className={styles.sectionIcon} />
                <span>Outputs</span>
                <span className={styles.sectionCount}>({outputs.length})</span>
              </div>
            </div>
            
            <div className={styles.simpleItemList}>
              {outputs.length === 0 ? (
                <Empty 
                  description="No outputs" 
                  image={Empty.PRESENTED_IMAGE_SIMPLE}
                  className={styles.emptyStateSmall}
                />
              ) : (
                outputs.map(output => (
                  <SimpleItem
                    key={output.id}
                    item={output}
                    icon={<ExportOutlined className={styles.simpleItemIcon} />}
                    onEdit={() => {/* TODO */}}
                    onDelete={() => {/* TODO */}}
                  />
                ))
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Resource Modal */}
      <Modal
        title={editingResource ? 'Edit Resource' : 'Add Resource'}
        open={resourceModalVisible}
        onOk={handleResourceSubmit}
        onCancel={() => setResourceModalVisible(false)}
        okText={editingResource ? 'Update' : 'Add'}
        className={styles.formModal}
      >
        <Form form={resourceForm} layout="vertical">
          <Form.Item 
            name="name" 
            label="Resource Name" 
            rules={[{ required: true, message: 'Please enter resource name' }]}
          >
            <Input placeholder="e.g., CRM System, Training Budget, IT Support" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea 
              placeholder="Describe this resource and its capabilities" 
              rows={3}
            />
          </Form.Item>
          <Form.Item name="cost" label="Cost ($)">
            <InputNumber 
              placeholder="0" 
              min={0}
              style={{ width: '100%' }}
              formatter={value => `$ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
            />
          </Form.Item>
          <Form.Item name="isExisting" valuePropName="checked">
            <Checkbox>This is an existing resource (not a new acquisition)</Checkbox>
          </Form.Item>
        </Form>
      </Modal>

      {/* Need Modal */}
      <Modal
        title={editingNeed ? 'Edit Need' : 'Add Need'}
        open={needModalVisible}
        onOk={handleNeedSubmit}
        onCancel={() => setNeedModalVisible(false)}
        okText={editingNeed ? 'Update' : 'Add'}
        className={styles.formModal}
      >
        <Form form={needForm} layout="vertical">
          <Form.Item 
            name="name" 
            label="Need Name" 
            rules={[{ required: true, message: 'Please enter need name' }]}
          >
            <Input placeholder="e.g., Reduce Manual Data Entry, Improve Report Accuracy" />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea 
              placeholder="Describe this need and its impact on the situation" 
              rows={3}
            />
          </Form.Item>
          <Form.Item 
            name="priority" 
            label="Priority"
            rules={[{ required: true, message: 'Please select priority' }]}
          >
            <Select placeholder="Select priority">
              <Select.Option value="high">High</Select.Option>
              <Select.Option value="medium">Medium</Select.Option>
              <Select.Option value="low">Low</Select.Option>
            </Select>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SituationPanel;
